import { useEffect } from 'react';

import { InfiniteData, useInfiniteQuery } from '@tanstack/react-query';
import { IDocumentListResponse } from '../../api/types';
import { IDocument } from '../../types/my-types';
import useGlynt from './useGlynt';
import { getOffsetFromUrl } from '../../utils/getOffsetFromUrl';

function mergeResults(data: InfiniteData<IDocumentListResponse> | undefined) {
  if (!data) return [];

  let results: IDocument[] = [];

  data.pages.map((page) => {
    results = results.concat(page.results);
    return true;
  });

  return results;
}

type ParamTypes = {
  groupId: string | undefined;
  group: 'train' | 'extract';
  dpid?: string;
};

// get all documents from an extraction batch
export default function useGetAllDocuments({ groupId, group, dpid }: ParamTypes) {
  const glynt = useGlynt(dpid);

  const limit = 150;

  const { data, isError, isLoading, isFetching, fetchNextPage, hasNextPage, refetch } =
    useInfiniteQuery({
      queryKey: ['allDocuments', group, groupId],
      queryFn: async ({ pageParam }) => {
        let queryParams =
          group === 'train'
            ? {
                advanced: true,
                ordering: '-created_at',
                limit,
                offset: pageParam,
                training_set: groupId as string,
              }
            : {
                advanced: true,
                limit,
                offset: pageParam,
                extraction_batch: groupId as string,
              };

        return (await glynt).document.list(queryParams);
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage) => {
        if (lastPage.next) {
          const offset = getOffsetFromUrl(lastPage.next);
          return offset;
        }
      },
      refetchOnWindowFocus: false,
      enabled: Boolean(groupId),
    });

  useEffect(() => {
    if (hasNextPage) {
      fetchNextPage();
    }
  }, [data, hasNextPage, fetchNextPage]);

  const currentPage = data?.pages?.length ?? 0;
  const totalPages = data?.pages?.[0]?.count ? Math.ceil(data.pages[0].count / limit) : 0;

  return {
    isLoading,
    isError,
    isFetching,
    data: mergeResults(data),
    currentPage,
    totalPages,
    refetch,
  };
}
